import { create } from "zustand";
import { ApprovalRequestType, ApprovalStatus } from "@/gql/graphql";

type ApprovalStatusType = {
  approvalRequestType: ApprovalRequestType;
  approvalStatus: ApprovalStatus;
};

type ApprovalStatusStore = {
  statusByContract: Record<string, ApprovalStatusType>;
  quoteReadyByContract: Record<string, boolean>;
  setApprovalStatus: (
    contractId: string,
    approvalRequestType: ApprovalRequestType,
    approvalStatus: ApprovalStatus
  ) => void;
  markQuoteReady: (contractId: string) => void;
  clearApprovalStatus: (
    contractId: string,
    approvalRequestType?: ApprovalRequestType
  ) => void;
};

export const useApprovalDeclinedStore = create<ApprovalStatusStore>((set) => ({
  statusByContract: {},
  quoteReadyByContract: {},
  setApprovalStatus: (contractId, approvalRequestType, approvalStatus) => {
    set((state) => {
      const existing = state.statusByContract[contractId];
      if (
        existing?.approvalRequestType === approvalRequestType &&
        existing.approvalStatus === approvalStatus
      ) {
        return state;
      }

      return {
        statusByContract: {
          ...state.statusByContract,
          [contractId]: { approvalRequestType, approvalStatus },
        },
        quoteReadyByContract: {
          ...state.quoteReadyByContract,
          [contractId]: false,
        },
      };
    });
  },
  markQuoteReady: (contractId) => {
    set((state) => {
      if (state.quoteReadyByContract[contractId]) {
        return state;
      }
      return {
        quoteReadyByContract: {
          ...state.quoteReadyByContract,
          [contractId]: true,
        },
      };
    });
  },
  clearApprovalStatus: (contractId, approvalRequestType) => {
    set((state) => {
      const existing = state.statusByContract[contractId];
      if (!existing) {
        return state;
      }
      if (
        approvalRequestType &&
        existing.approvalRequestType !== approvalRequestType
      ) {
        return state;
      }

      const { [contractId]: _removed, ...statusByContract } =
        state.statusByContract;
      const { [contractId]: _readyRemoved, ...quoteReadyByContract } =
        state.quoteReadyByContract;
      return { statusByContract, quoteReadyByContract };
    });
  },
}));

function useRealtimeApprovalStatus(
  contractId: string | null | undefined,
  approvalRequestType: ApprovalRequestType
) {
  return useApprovalDeclinedStore((state) => {
    const entry = contractId ? state.statusByContract[contractId] : undefined;
    return entry?.approvalRequestType === approvalRequestType
      ? entry.approvalStatus
      : undefined;
  });
}

export function useRealtimeDiscountApprovalStatus(contractId?: string | null) {
  return useRealtimeApprovalStatus(contractId, ApprovalRequestType.Discount);
}

export function useRealtimeBillingApprovalStatus(contractId?: string | null) {
  return useRealtimeApprovalStatus(
    contractId,
    ApprovalRequestType.BillingFrequency
  );
}

export function useHasRealtimeDiscountDeclined(contractId?: string | null) {
  return (
    useRealtimeDiscountApprovalStatus(contractId) === ApprovalStatus.Rejected
  );
}

export function useHasRealtimeBillingDeclined(contractId?: string | null) {
  return (
    useRealtimeBillingApprovalStatus(contractId) === ApprovalStatus.Rejected
  );
}

export function useHasRealtimeDiscountDeclinedReady(
  contractId?: string | null
) {
  return useApprovalDeclinedStore((state) => {
    if (!contractId) return false;
    const entry = state.statusByContract[contractId];
    return (
      entry?.approvalRequestType === ApprovalRequestType.Discount &&
      entry.approvalStatus === ApprovalStatus.Rejected &&
      state.quoteReadyByContract[contractId] === true
    );
  });
}

export function useHasRealtimeBillingDeclinedReady(contractId?: string | null) {
  return useApprovalDeclinedStore((state) => {
    if (!contractId) return false;
    const entry = state.statusByContract[contractId];
    return (
      entry?.approvalRequestType === ApprovalRequestType.BillingFrequency &&
      entry.approvalStatus === ApprovalStatus.Rejected &&
      state.quoteReadyByContract[contractId] === true
    );
  });
}
